import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, ArrowRight, Check, Palette } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MasterAccess from '../components/MasterAccess';

const API = process.env.REACT_APP_BACKEND_URL;

const PostaJaConfigurar = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planoInicial = searchParams.get('plano') || 'basico';
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [form, setForm] = useState({
    nome_negocio: '',
    nome: '',
    whatsapp: '',
    email: '',
    nicho: '',
    publico_alvo: '',
    plataformas: ['instagram'],
    estilo_visual: 'moderno',
    tom_marca: 'profissional',
    objetivo: 'engajamento',
    cor_primaria: '#6D28D9',
    cor_secundaria: '#F5F3FF',
    cor_destaque: '#FBBF24',
    bordao: '',
    plano: planoInicial
  });

  const estilos = [
    { value: 'clean', label: 'Clean', desc: 'Minimalista e elegante' },
    { value: 'vibrante', label: 'Vibrante', desc: 'Cores fortes e chamativas' },
    { value: 'elegante', label: 'Elegante', desc: 'Sofisticado e premium' },
    { value: 'moderno', label: 'Moderno', desc: 'Atual e profissional' },
    { value: 'minimalista', label: 'Minimalista', desc: 'Simples e direto' }
  ];

  const tons = [
    { value: 'profissional', label: 'Profissional', desc: 'Sério e confiável' },
    { value: 'descontraido', label: 'Descontraído', desc: 'Leve e amigável' },
    { value: 'inspirador', label: 'Inspirador', desc: 'Motivacional' },
    { value: 'educativo', label: 'Educativo', desc: 'Informativo' },
    { value: 'divertido', label: 'Divertido', desc: 'Bem-humorado' }
  ];

  const objetivos = [
    { value: 'vendas', label: 'Vendas', desc: 'Converter seguidores em clientes' },
    { value: 'autoridade', label: 'Autoridade', desc: 'Ser referência no nicho' },
    { value: 'engajamento', label: 'Engajamento', desc: 'Mais curtidas e comentários' },
    { value: 'reconhecimento', label: 'Reconhecimento', desc: 'Aumentar a visibilidade' }
  ];

  const plataformasOpcoes = [
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'whatsapp', label: 'WhatsApp Status' },
    { value: 'facebook', label: 'Facebook' }
  ];

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API}/api/postaja/clientes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await res.json();
      
      if (data.success) {
        navigate(`/postaja/painel/${data.slug}`);
      } else {
        setError(data.detail || 'Erro ao criar conta');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setStep(s => Math.min(s + 1, 4));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const togglePlataforma = (p) => {
    if (form.plataformas.includes(p)) {
      setForm({ ...form, plataformas: form.plataformas.filter(x => x !== p) });
    } else {
      setForm({ ...form, plataformas: [...form.plataformas, p] });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Header />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4].map(s => (
              <div 
                key={s} 
                className={`w-3 h-3 rounded-full transition-all ${s <= step ? 'bg-violet-600' : 'bg-slate-200'}`}
              />
            ))}
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="text-violet-600" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              {step === 1 && 'Seus dados'}
              {step === 2 && 'Sobre seu negócio'}
              {step === 3 && 'Estilo e cores'}
              {step === 4 && 'Confirmar'}
            </h1>
            <p className="text-slate-500 mt-2">Passo {step} de 4</p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            {/* Step 1: Dados pessoais */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label>Nome do negócio *</Label>
                  <Input
                    data-testid="input-nome-negocio"
                    placeholder="Ex: Loja da Maria"
                    value={form.nome_negocio}
                    onChange={(e) => setForm({...form, nome_negocio: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Seu nome *</Label>
                  <Input
                    data-testid="input-nome"
                    placeholder="Maria Silva"
                    value={form.nome}
                    onChange={(e) => setForm({...form, nome: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>WhatsApp *</Label>
                  <Input
                    data-testid="input-whatsapp"
                    placeholder="11 99999-9999"
                    value={form.whatsapp}
                    onChange={(e) => setForm({...form, whatsapp: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    data-testid="input-email"
                    type="email"
                    placeholder="maria@email.com"
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    required
                  />
                </div>
              </div>
            )}

            {/* Step 2: Sobre o negócio */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <Label>Qual seu nicho ou o que você vende? *</Label>
                  <Textarea
                    data-testid="input-nicho"
                    placeholder="Ex: Loja de roupas femininas, moda casual e fitness"
                    value={form.nicho}
                    onChange={(e) => setForm({...form, nicho: e.target.value})}
                    rows={2}
                    required
                  />
                </div>
                <div>
                  <Label>Para quem são os conteúdos? (público-alvo) *</Label>
                  <Textarea
                    data-testid="input-publico"
                    placeholder="Ex: Mulheres de 25 a 45 anos, que buscam praticidade e estilo"
                    value={form.publico_alvo}
                    onChange={(e) => setForm({...form, publico_alvo: e.target.value})}
                    rows={2}
                    required
                  />
                </div>
                <div>
                  <Label>Tem algum bordão ou frase marcante?</Label>
                  <Input
                    data-testid="input-bordao"
                    placeholder="Ex: Vista-se de confiança!"
                    value={form.bordao}
                    onChange={(e) => setForm({...form, bordao: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Onde vai usar o conteúdo?</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {plataformasOpcoes.map(p => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => togglePlataforma(p.value)}
                        className={`px-4 py-2 rounded-lg border transition-all ${
                          form.plataformas.includes(p.value) 
                            ? 'bg-violet-100 border-violet-500 text-violet-700' 
                            : 'border-slate-200 text-slate-600 hover:border-violet-300'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Estilo e cores */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <Label>Estilo visual</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {estilos.map(e => (
                      <button
                        key={e.value}
                        type="button"
                        onClick={() => setForm({...form, estilo_visual: e.value})}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          form.estilo_visual === e.value 
                            ? 'bg-violet-100 border-violet-500' 
                            : 'border-slate-200 hover:border-violet-300'
                        }`}
                      >
                        <div className="font-medium text-slate-900">{e.label}</div>
                        <div className="text-xs text-slate-500">{e.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Tom da comunicação</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {tons.map(t => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setForm({...form, tom_marca: t.value})}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          form.tom_marca === t.value 
                            ? 'bg-violet-100 border-violet-500' 
                            : 'border-slate-200 hover:border-violet-300'
                        }`}
                      >
                        <div className="font-medium text-slate-900">{t.label}</div>
                        <div className="text-xs text-slate-500">{t.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Objetivo principal</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {objetivos.map(o => (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setForm({...form, objetivo: o.value})}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          form.objetivo === o.value 
                            ? 'bg-violet-100 border-violet-500' 
                            : 'border-slate-200 hover:border-violet-300'
                        }`}
                      >
                        <div className="font-medium text-slate-900">{o.label}</div>
                        <div className="text-xs text-slate-500">{o.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Palette size={16} /> Cores da sua marca
                  </Label>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Primária</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.cor_primaria}
                          onChange={(e) => setForm({...form, cor_primaria: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={form.cor_primaria}
                          onChange={(e) => setForm({...form, cor_primaria: e.target.value})}
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Secundária</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.cor_secundaria}
                          onChange={(e) => setForm({...form, cor_secundaria: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={form.cor_secundaria}
                          onChange={(e) => setForm({...form, cor_secundaria: e.target.value})}
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Destaque</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.cor_destaque}
                          onChange={(e) => setForm({...form, cor_destaque: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={form.cor_destaque}
                          onChange={(e) => setForm({...form, cor_destaque: e.target.value})}
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div className="mt-4 p-4 rounded-lg border border-slate-200" style={{ background: form.cor_secundaria }}>
                    <div className="text-sm font-medium" style={{ color: form.cor_primaria }}>
                      Preview das suas cores
                    </div>
                    <div className="mt-2 flex gap-2">
                      <div className="px-3 py-1 rounded text-white text-sm" style={{ background: form.cor_primaria }}>
                        Primária
                      </div>
                      <div className="px-3 py-1 rounded text-white text-sm" style={{ background: form.cor_destaque }}>
                        Destaque
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Confirmar */}
            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <Label>Escolha seu plano</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <button
                      type="button"
                      onClick={() => setForm({...form, plano: 'basico'})}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        form.plano === 'basico' 
                          ? 'border-violet-500 bg-violet-50' 
                          : 'border-slate-200 hover:border-violet-300'
                      }`}
                    >
                      <div className="font-bold text-slate-900">Básico</div>
                      <div className="text-2xl font-bold text-violet-600">R$ 49,99<span className="text-sm">/mês</span></div>
                      <div className="text-xs text-slate-500 mt-2">38 conteúdos/mês</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({...form, plano: 'plus'})}
                      className={`p-4 rounded-xl border-2 text-left transition-all relative ${
                        form.plano === 'plus' 
                          ? 'border-violet-500 bg-violet-50' 
                          : 'border-slate-200 hover:border-violet-300'
                      }`}
                    >
                      <div className="absolute -top-2 right-2 bg-violet-500 text-white text-xs px-2 py-0.5 rounded-full">
                        Popular
                      </div>
                      <div className="font-bold text-slate-900">Plus</div>
                      <div className="text-2xl font-bold text-violet-600">R$ 79,99<span className="text-sm">/mês</span></div>
                      <div className="text-xs text-slate-500 mt-2">48 conteúdos/mês</div>
                    </button>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <h3 className="font-semibold text-slate-900">Resumo</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-slate-500">Negócio:</span>
                    <span className="text-slate-900">{form.nome_negocio || '-'}</span>
                    <span className="text-slate-500">Nicho:</span>
                    <span className="text-slate-900">{form.nicho || '-'}</span>
                    <span className="text-slate-500">Estilo:</span>
                    <span className="text-slate-900">{form.estilo_visual}</span>
                    <span className="text-slate-500">Tom:</span>
                    <span className="text-slate-900">{form.tom_marca}</span>
                    <span className="text-slate-500">Plano:</span>
                    <span className="text-violet-600 font-medium">{form.plano === 'plus' ? 'Plus - R$ 79,99/mês' : 'Básico - R$ 49,99/mês'}</span>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <div className="w-6 h-6 rounded" style={{ background: form.cor_primaria }} title="Primária" />
                    <div className="w-6 h-6 rounded" style={{ background: form.cor_secundaria }} title="Secundária" />
                    <div className="w-6 h-6 rounded" style={{ background: form.cor_destaque }} title="Destaque" />
                  </div>
                </div>

                {error && (
                  <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">
                    {error}
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-4 mt-8">
              {step > 1 && (
                <Button variant="outline" onClick={prevStep} className="flex-1">
                  Voltar
                </Button>
              )}
              {step < 4 ? (
                <Button onClick={nextStep} className="flex-1 bg-violet-600 hover:bg-violet-700">
                  Próximo
                  <ArrowRight size={18} />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  className="flex-1 bg-violet-600 hover:bg-violet-700"
                  disabled={loading}
                  data-testid="btn-finalizar"
                >
                  {loading ? 'Criando...' : 'Criar minha conta'}
                  <Check size={18} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Master Access */}
      <MasterAccess 
        produto="postaja" 
        slug={form.nome_negocio.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 20)}
        onSuccess={() => navigate(`/postaja/painel/${form.nome_negocio.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 20)}`)}
      />
    </div>
  );
};

export default PostaJaConfigurar;
